import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Flag as FlagIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { Goal, Team } from '../../types';
import { goalService } from '../../services/goalService';
import { teamService } from '../../services/teamService';
import { useAuth } from '../../context/AuthContext';
import CreateGoalDialog from './CreateGoalDialog';
import EditGoalDialog from './EditGoalDialog';

const GoalsPage: React.FC = () => {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    
    setLoading(true);
    setError('');
    
    try {
      if (user.role === 'athlete') {
        // Load athlete's goals and teams
        const [goalsData, teamsData] = await Promise.all([
          goalService.getGoalsByAthlete(user.id),
          teamService.getTeamsByAthlete(user.id)
        ]);
        setGoals(goalsData);
        setTeams(teamsData);
      } else if (user.role === 'coach' || user.role === 'headCoach') {
        // Load all goals for coach's/headCoach's teams
        const teamsData = await teamService.getTeamsByCoach(user.id);
        setTeams(teamsData);
        
        if (teamsData.length > 0) {
          const allGoals: Goal[] = [];
          for (const team of teamsData) {
            const teamGoals = await goalService.getGoalsByTeam(team.id);
            allGoals.push(...teamGoals);
          }
          setGoals(allGoals.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds));
        }
      }
    } catch (err: any) {
      console.error('Error loading goals:', err);
      setError(err.message || 'Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  const handleGoalCreated = () => {
    loadData();
    setCreateDialogOpen(false);
  };

  const handleGoalUpdated = () => {
    loadData();
    setEditDialogOpen(false);
    setSelectedGoal(null);
  };

  const handleGoalDeleted = async () => {
    if (!selectedGoalId) return;
    
    try {
      await goalService.deleteGoal(selectedGoalId);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete goal');
    } finally {
      setAnchorEl(null);
      setSelectedGoalId(null);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, goalId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedGoalId(goalId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedGoalId(null);
  };

  const handleEditClick = async () => {
    if (!selectedGoalId) return;
    
    try {
      const goal = await goalService.getGoalById(selectedGoalId);
      if (goal) {
        setSelectedGoal(goal);
        setEditDialogOpen(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load goal for editing');
    } finally {
      handleMenuClose();
    }
  };

  const handleDeleteClick = () => {
    if (window.confirm('Are you sure you want to delete this goal? This action cannot be undone.')) {
      handleGoalDeleted();
    } else {
      handleMenuClose();
    }
  };

  const getTeamName = (teamId: string) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Unknown Team';
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getGoalTypeColor = (goalType: string) => {
    switch (goalType) {
      case 'distance': return 'primary';
      case 'time': return 'secondary';
      case 'pace': return 'success';
      case 'frequency': return 'warning';
      case 'custom': return 'info';
      default: return 'default';
    }
  };

  const getGoalTypeIcon = (goalType: string) => {
    switch (goalType) {
      case 'distance': return '🏃';
      case 'time': return '⏱️';
      case 'pace': return '⚡';
      case 'frequency': return '📅';
      case 'custom': return '🎯';
      default: return '🎯';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" component="h1">
          {user?.role === 'athlete' ? 'My Goals' : 'Team Goals'}
        </Typography>
        
        {teams.length > 0 && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            size="large"
          >
            Create Goal
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {teams.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {user?.role === 'athlete' 
            ? 'You need to join a team before you can create goals. Go to the Teams page to join a team using an invitation code.'
            : 'Create a team first before you can create goals. Go to the Teams page to create a team.'
          }
        </Alert>
      )}

      {goals.length === 0 && teams.length > 0 ? (
        <Box sx={{ 
          textAlign: 'center', 
          py: 4,
          bgcolor: 'grey.50',
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'grey.300'
        }}>
          <FlagIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No goals created yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.role === 'athlete' 
              ? 'Set your first running goal to start tracking your progress!'
              : 'Athletes haven\'t created any goals yet.'
            }
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {goals.map((goal) => {
            const progress = goalService.calculateProgressPercentage(goal);
            const status = goalService.getGoalStatusText(goal);
            const isCompleted = goal.isCompleted;
            
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={goal.id}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  position: 'relative',
                  ...(isCompleted && {
                    bgcolor: 'success.50',
                    border: '2px solid',
                    borderColor: 'success.main',
                  })
                }}>
                  {isCompleted && (
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 8, 
                      right: 8,
                      zIndex: 1
                    }}>
                      <CheckCircleIcon color="success" />
                    </Box>
                  )}
                  
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start', 
                      mb: 2
                    }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                          {goal.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {getTeamName(goal.teamId)}
                        </Typography>
                        {goal.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {goal.description}
                          </Typography>
                        )}
                      </Box>
                      
                      {user?.role === 'athlete' && user?.id === goal.athleteId && (
                        <IconButton 
                          onClick={(e) => handleMenuOpen(e, goal.id)} 
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h4" component="span">
                        {getGoalTypeIcon(goal.goalType)}
                      </Typography>
                      <Chip
                        label={goalService.getGoalTypeOptions().find(opt => opt.value === goal.goalType)?.label || goal.goalType}
                        color={getGoalTypeColor(goal.goalType)}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Progress
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {progress.toFixed(1)}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={progress} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            bgcolor: isCompleted ? 'success.main' : 'primary.main'
                          }
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Current:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {goalService.formatGoalValue(goal.currentValue, goal.unit, goal.goalType)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Target:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {goalService.formatGoalValue(goal.targetValue, goal.unit, goal.goalType)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Status:
                      </Typography>
                      <Chip 
                        label={status} 
                        color={isCompleted ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'text.secondary' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ScheduleIcon sx={{ fontSize: 16 }} />
                        <span>Start: {formatDate(goal.startDate)}</span>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FlagIcon sx={{ fontSize: 16 }} />
                        <span>End: {formatDate(goal.endDate)}</span>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create Goal Dialog */}
      <CreateGoalDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onGoalCreated={handleGoalCreated}
        teams={teams}
      />

      {/* Edit Goal Dialog */}
      {selectedGoal && (
        <EditGoalDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedGoal(null);
          }}
          onGoalUpdated={handleGoalUpdated}
          goal={selectedGoal}
          teams={teams}
        />
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Goal</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Goal</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default GoalsPage;
